import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Star, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const ReviewsList = ({ reviews = [], propertyId, hostId, currentUser, onReplySubmit }) => {
  const { t, i18n } = useTranslation();
  const language = i18n.language === 'pt-BR' || i18n.language === 'pt' ? 'pt' : 'en';
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Translations
  const texts = {
    pt: {
      noReviews: 'Nenhuma avaliação ainda. Seja o primeiro a avaliar!',
      cleanliness: 'Limpeza',
      communication: 'Comunicação',
      accuracy: 'Precisão',
      location: 'Localização',
      value: 'Custo-Benefício',
      hostReply: 'Resposta do Anfitrião',
      writePlaceholder: 'Escreva sua resposta...',
      submitting: 'Enviando...',
      submitReply: 'Publicar Resposta',
      cancel: 'Cancelar',
      reply: 'Responder'
    },
    en: {
      noReviews: 'No reviews yet. Be the first to review!',
      cleanliness: 'Cleanliness',
      communication: 'Communication',
      accuracy: 'Accuracy',
      location: 'Location',
      value: 'Value',
      hostReply: 'Host Reply',
      writePlaceholder: 'Write your reply...',
      submitting: 'Submitting...',
      submitReply: 'Submit Reply',
      cancel: 'Cancel',
      reply: 'Reply'
    }
  };
  const tx = texts[language] || texts.en;

  // Format date based on locale
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const locale = language === 'pt' ? 'pt-BR' : 'en-US';
    return new Date(dateStr).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleReplySubmit = async (reviewId) => {
    if (!replyText.trim()) return;
    
    setSubmitting(true);
    try {
      await onReplySubmit(reviewId, replyText);
      setReplyText('');
      setReplyingTo(null);
    } catch (error) {
      console.error('Error submitting reply:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${star <= rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`}
          />
        ))}
      </div>
    );
  };

  if (reviews.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p>{tx.noReviews}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {reviews.map((review) => {
        // Check if current user is the host of this property (compare both id and user_id for compatibility)
        const currentUserId = currentUser?.id || currentUser?.user_id;
        const canReply = currentUser && currentUser.role === 'host' && currentUserId === hostId && !review.host_reply;
        const isReplying = replyingTo === review.id;

        return (
          <Card key={review.id}>
            <CardContent className="p-6">
              {/* Review Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold">{review.user_name || review.guest_name || 'Anônimo'}</div>
                    <div className="text-sm text-muted-foreground">
                      {formatDate(review.created_at)}
                    </div>
                  </div>
                </div>
                {renderStars(review.rating || review.rating_overall)}
              </div>

              {/* Detailed Ratings */}
              {(review.rating_cleanliness || review.rating_communication || review.rating_location) && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4 text-sm">
                  {review.rating_cleanliness && (
                    <div>
                      <div className="text-muted-foreground">{tx.cleanliness}</div>
                      <div className="font-medium">{review.rating_cleanliness}/5</div>
                    </div>
                  )}
                  {review.rating_communication && (
                    <div>
                      <div className="text-muted-foreground">{tx.communication}</div>
                      <div className="font-medium">{review.rating_communication}/5</div>
                    </div>
                  )}
                  {review.rating_accuracy && (
                    <div>
                      <div className="text-muted-foreground">{tx.accuracy}</div>
                      <div className="font-medium">{review.rating_accuracy}/5</div>
                    </div>
                  )}
                  {review.rating_location && (
                    <div>
                      <div className="text-muted-foreground">{tx.location}</div>
                      <div className="font-medium">{review.rating_location}/5</div>
                    </div>
                  )}
                  {review.rating_value && (
                    <div>
                      <div className="text-muted-foreground">{tx.value}</div>
                      <div className="font-medium">{review.rating_value}/5</div>
                    </div>
                  )}
                </div>
              )}

              {/* Comment */}
              <p className="text-muted-foreground leading-relaxed">{review.comment}</p>

              {/* Host Reply */}
              {review.host_reply && (
                <div className="mt-4 pt-4 border-t bg-muted/30 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <div className="font-semibold text-sm">{tx.hostReply}</div>
                      <div className="text-xs text-muted-foreground">
                        {formatDate(review.host_reply.created_at)}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm">{review.host_reply.reply_text}</p>
                </div>
              )}

              {/* Reply Form */}
              {canReply && (
                <div className="mt-4 pt-4 border-t">
                  {isReplying ? (
                    <div className="space-y-3">
                      <Textarea
                        placeholder={tx.writePlaceholder}
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        rows={3}
                        className="resize-none"
                      />
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleReplySubmit(review.id)}
                          disabled={submitting || !replyText.trim()}
                          size="sm"
                        >
                          {submitting ? tx.submitting : tx.submitReply}
                        </Button>
                        <Button
                          onClick={() => {
                            setReplyingTo(null);
                            setReplyText('');
                          }}
                          variant="outline"
                          size="sm"
                        >
                          {tx.cancel}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      onClick={() => setReplyingTo(review.id)}
                      variant="outline"
                      size="sm"
                    >
                      {tx.reply}
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};